import React, { useRef, useEffect, useState, FC, ChangeEvent } from 'react';
import Cropper from 'cropperjs';
import { css } from 'emotion';

import useStylesheets from '@vromo/hooks/useStylesheets';
import { noop } from 'lodash';

interface Props {
    onChange?: (blob: string, dataUri: string) => void;
    maxWidth?: number;
    maxHeight?: number;
    aspectRatio?: number;
    defaultUrl?: string;
    editUrl?: string;
}

import CropperStyle from 'cropperjs/dist/cropper.css';

const ResizableImageEditor: FC<Props> = ({
    onChange = noop,
    maxWidth,
    maxHeight,
    aspectRatio,
    defaultUrl,
    editUrl
}) => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    useStylesheets(CropperStyle);

    const imageRef = useRef(null);
    const cropper = useRef(null);

    const [url, setUrl] = useState(() => (editUrl ? editUrl : defaultUrl));

    function update() {
        const dataUri = cropper.current
            .getCroppedCanvas({ maxWidth, maxHeight })
            .toDataURL('image/png');

        cropper.current.getCroppedCanvas({ maxWidth, maxHeight }).toBlob((blob) => {
            // blob
            onChange(blob, dataUri);
        });
    }

    function createCropper() {
        return new Cropper(imageRef.current, {
            aspectRatio,
            viewMode: 1,
            ready() {
                update();
            },
            cropend() {
                update();
            }
        });
    }

    useEffect(() => {
        cropper.current = createCropper();

        return () => {
            cropper.current.destroy();
        };
    }, []);

    useEffect(() => {
        if (url && cropper.current) {
            cropper.current.replace(url);
        }
    }, [url]);

    function clear() {
        setUrl(null);
        cropper.current.destroy();
        cropper.current = createCropper();

        onChange(null, null);
    }

    function load(e: ChangeEvent<HTMLInputElement>) {
        if (e.target.files.length) {
            const file = e.target.files[0];

            const reader = new FileReader();

            reader.onload = (e2) => {
                setUrl(e2.target.result as string);
            };

            reader.readAsDataURL(file);
        }
    }

    return (
        <div
            className={css`
                margin: 4px;
                max-width: 300px;
                img {
                    max-width: 100%;
                }
            `}>
            <img ref={imageRef} src={url} />
            {url ? (
                <a onClick={clear} style={{ display: 'block' }}>
                    clear
                </a>
            ) : null}
            <input type="file" onChange={load} accept="image/png, image/jpeg" />
        </div>
    );
};

export default ResizableImageEditor;
